/**
 * VideoService - Thống nhất quản lý video trong toàn bộ ứng dụng
 * 
 * Mục đích:
 * - Thống nhất logic tạo đường dẫn video
 * - Tránh trùng lặp code giữa Menu mô phỏng và Bài học
 * - Quản lý tập trung danh sách video có sẵn (tự động scan từ public/videos)
 * - Đảm bảo tính nhất quán trong toàn bộ ứng dụng
 */

// Import danh sách video được tự động generate bởi scripts/scan-videos.js
import availableVideosData from '../../data/availableVideos.json'

import { API_CONFIG } from '../../config/api.config'

export interface VideoInfo {
  id: string
  title: string
  lop: number
  chuong: number
  bai: number
  videoPath: string
  exists: boolean
  filename?: string
  fileSize?: number
}

export class VideoService {
  // Danh sách video được tự động load từ file JSON
  private static readonly AVAILABLE_VIDEOS: Omit<VideoInfo, 'exists'>[] = availableVideosData.videos

  /**
   * Tạo đường dẫn video từ tham số bài học
   * Đây là logic thống nhất cho toàn bộ ứng dụng
   */
  static generateVideoPath(lop: string, chuong: string, bai: string): string {
    // Map class numbers to folder names
    const classFolders: Record<string, string> = {
      '10': 'lop-10',
      '11': 'lop-11',
      '12': 'lop-12'
    }

    // Map chapter numbers to folder names
    const chapterFolders: Record<string, Record<string, string>> = {
      '10': {
        '1': 'chuong-1-dong-hoc',
        '2': 'chuong-2-dong-luc-hoc',
        '3': 'chuong-3-nang-luong-va-cong',
        '4': 'chuong-4-chuyen-dong-tron-va-bien-dang'
      },
      '11': {
        '1': 'chuong-1-dao-dong',
        '2': 'chuong-2-song',
        '3': 'chuong-3-dien-truong',
        '4': 'chuong-4-dong-dien-mach-dien'
      },
      '12': {
        '1': 'chuong-1-vat-li-nhiet',
        '2': 'chuong-2-khi-li-tuong',
        '3': 'chuong-3-tu-truong',
        '4': 'chuong-4-vat-li-hat-nhan'
      }
    }

    const lopFolder = classFolders[lop] || `lop-${lop}`
    const chuongFolder = chapterFolders[lop]?.[chuong] || `chuong-${chuong}`

    // Lesson name mapping - đầy đủ cho tất cả các bài học
    const lessonNames: Record<string, Record<string, Record<string, string>>> = {
      '10': {
        '0': {
          '1': 'bai-1-mo-dau-vat-li'
        },
        '1': {
          '1': 'bai-1-chuyen-dong-co',
          '2': 'bai-2-chuyen-dong-thang-deu',
          '3': 'bai-3-chuyen-dong-thang-bien-doi-deu',
          '4': 'bai-4-suc-can-cua-khong-khi',
          '5': 'bai-5-chuyen-dong-bien-doi'
        },
        '2': {
          '1': 'bai-1-dong-luc-hoc',
          '2': 'bai-2-luc-dan-hoi',
          '3': 'bai-3-luc-ma-sat'
        },
        '3': {
          '1': 'bai-1-cong-va-cong-suat',
          '2': 'bai-2-dong-nang',
          '3': 'bai-3-the-nang',
          '4': 'bai-4-co-nang'
        },
        '4': {
          '1': 'bai-1-chuyen-dong-tron-deu',
          '2': 'bai-2-luc-huong-tam',
          '3': 'bai-3-chuyen-dong-tron-deu-trong-thuc-te'
        }
      },
      '11': {
        '1': {
          '1': 'bai-1-dao-dong-dieu-hoa-phuong-trinh-dao-dong-dieu-hoa',
          '2': 'bai-2-van-toc-gia-toc-trong-dao-dong-dieu-hoa',
          '3': 'bai-3-nang-luong-trong-dao-dong-dieu-hoa',
          '4': 'bai-4-dao-dong-tat-dan-cuong-buc-cong-huong'
        },
        '2': {
          '1': 'bai-1-song-co-hoc',
          '2': 'bai-2-phuong-trinh-song',
          '3': 'bai-3-song-am',
          '4': 'bai-4-dac-trung-vat-li-cua-am',
          '5': 'bai-5-su-truyen-song-co',
          '6': 'bai-6_Hieu_ung_Doppler',
          '7': 'bai-7_Song_dien_tu',
          '8': 'Bai-8_Giao_thoa_song',
          '9': 'Bai-9_Song_dung'
        },
        '3': {
          '1': 'bai-1-dien-truong',
          '2': 'bai-2-luc-dien',
          '3': 'bai-3-dien-truong-va-dien-the',
          '4': 'bai-4-cong-cua-luc-dien',
          '5': 'bai-5-tu-dien'
        },
        '4': {
          '1': 'bai-1-dong-dien-khong-doi',
          '2': 'bai-2-dien-tro-cua-day-dan',
          '3': 'bai-3-dinh-luat-om-doi-voi-toan-mach',
          '4': 'bai-4-dinh-luat-om-doi-voi-cac-loai-mach-dien'
        }
      },
      '12': {
        '1': {
          '1': 'bai-1-cau-truc-hat-nhan',
          '2': 'bai-2-nang-luong-lien-ket-hat-nhan',
          '3': 'bai-3-phan-ung-hat-nhan'
        },
        '2': {
          '1': 'bai-1-khi-li-tuong',
          '2': 'bai-2-qua-trinh-dang-nhiet',
          '3': 'bai-3-qua-trinh-dang-tich',
          '4': 'bai-4-qua-trinh-dang-ap',
          '7': 'bai_7_mo_hinh_dong_hoc_chat_khi',
          '8': 'bai-8-dinh-luat-boyle'
        },
        '3': {
          '1': 'bai-1-tu-truong',
          '2': 'bai-2-luc-tu',
          '3': 'bai-3-cam-ung-tu',
          '4': 'bai-4-tu-thong',
          '5': 'bai-5-suc-dien-dong-cam-ung',
          '6': 'bai-6-dong-dien-fuco',
          '7': 'bai-7-tu-truong-cua-dong-dien',
          '8': 'bai-8-luc-lo-ren-xo',
          '9': 'bai-9-dong-dien-trong-khong-gian',
          '10': 'bai-10-tu-truong-cua-dong-dien-trong-khong-gian',
          '11': 'bai-11-luc-tu-tac-dung-len-dong-dien',
          '12': 'bai-12-dong-dien-trong-tu-truong',
          '13': 'bai-13-hien-tuong-cam-ung-dien-tu',
          '14': 'bai-14-dong-dien-xoay-chieu',
          '15': 'Bai_15_Song_dien_tu'
        },
        '4': {
          '1': 'bai-1-hien-tuong-quang-dien',
          '2': 'bai-2-thuyet-luong-tu-anh-sang',
          '3': 'bai-3-hien-tuong-quang-phat-quang',
          '5': 'bai-5-tia-x'
        }
      }
    }

    let baiName = bai
    if (bai && !bai.startsWith('bai-')) {
      const fullName = lessonNames[lop]?.[chuong]?.[bai]
      if (fullName) {
        baiName = fullName
      }
    }

    const baseUrl = this.getVideoBaseUrl()
    const videoPath = `${baseUrl}/${lopFolder}/${chuongFolder}/${baiName}.mp4`

    console.log(`📹 VideoService: Generated video path (${import.meta.env.MODE}):`, videoPath)
    return videoPath
  }

  /**
   * Get the base URL for videos depending on environment
   * Now uses centralized API_CONFIG for consistency
   */
  static getVideoBaseUrl(): string {
    return API_CONFIG.videos.baseUrl
  }

  /**
   * Helper to transform video info with correct path
   */
  private static transformVideoInfo(video: Omit<VideoInfo, 'exists'>): VideoInfo {
    // Determine base URL
    const baseUrl = this.getVideoBaseUrl()

    // If video object already has a path, use it (adjusting for environment)
    // availableVideos.json paths start with /Videos/...
    let finalPath = video.videoPath

    if (import.meta.env.PROD) {
      // In production, we need to prepend the full domain if it's not already there
      // availableVideos.json paths are like /Videos/lop-11/...
      // baseUrl is https://vatli365.vn/bank/Videos
      // We need to replace /Videos with baseUrl
      if (finalPath.startsWith('/Videos')) {
        finalPath = finalPath.replace('/Videos', baseUrl)
      }
    }

    return {
      ...video,
      videoPath: finalPath,
      exists: true
    }
  }

  /**
   * Kiểm tra xem video có tồn tại trong danh sách có sẵn không
   */
  static getVideoInfo(lop: number, chuong: number, bai: number): VideoInfo | null {
    const video = this.AVAILABLE_VIDEOS.find(v =>
      v.lop === lop && v.chuong === chuong && v.bai === bai
    )

    if (!video) {
      return null
    }

    return this.transformVideoInfo(video)
  }

  /**
   * Lấy tất cả video cho một bài học cụ thể
   */
  static getVideosForLesson(lop: number, chuong: number, bai: number): VideoInfo[] {
    return this.AVAILABLE_VIDEOS
      .filter(v => v.lop === lop && v.chuong === chuong && v.bai === bai)
      .map(video => this.transformVideoInfo(video))
  }

  /**
   * Lấy tất cả video có sẵn
   */
  static getAllAvailableVideos(): VideoInfo[] {
    return this.AVAILABLE_VIDEOS.map(video => this.transformVideoInfo(video))
  }

  /**
   * Lấy video theo lớp
   */
  static getVideosByClass(lop: number): VideoInfo[] {
    return this.AVAILABLE_VIDEOS
      .filter(video => video.lop === lop)
      .map(video => this.transformVideoInfo(video))
  }

  /**
   * Lấy video theo lớp và chương
   */
  static getVideosByClassAndChapter(lop: number, chuong: number): VideoInfo[] {
    return this.AVAILABLE_VIDEOS
      .filter(video => video.lop === lop && video.chuong === chuong)
      .map(video => this.transformVideoInfo(video))
  }

  /**
   * Tìm video theo từ khóa
   */
  static searchVideos(searchTerm: string): VideoInfo[] {
    const term = searchTerm.toLowerCase()
    return this.AVAILABLE_VIDEOS
      .filter(video =>
        video.title.toLowerCase().includes(term) ||
        video.id.toLowerCase().includes(term)
      )
      .map(video => this.transformVideoInfo(video))
  }

  /**
   * Kiểm tra xem có video cho bài học cụ thể không
   */
  static hasVideoForLesson(lop: number, chuong: number, bai: number): boolean {
    return this.AVAILABLE_VIDEOS.some(video =>
      video.lop === lop && video.chuong === chuong && video.bai === bai
    )
  }

  /**
   * Refresh danh sách video - để reload sau khi scan-videos
   * (Cần restart dev server để áp dụng)
   */
  static getVideoCount(): number {
    return this.AVAILABLE_VIDEOS.length
  }

  /**
   * Get summary by class
   */
  static getSummaryByClass(): Record<number, number> {
    const summary: Record<number, number> = {}
    this.AVAILABLE_VIDEOS.forEach(video => {
      if (!summary[video.lop]) {
        summary[video.lop] = 0
      }
      summary[video.lop]++
    })
    return summary
  }
}
